import * as React from 'react';
import DOMPurify from 'dompurify';
import { useSelector } from 'react-redux';
import { Formik, FormikErrors, FormikProps, useFormikContext } from 'formik';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { hasCandidateRole, hasCustomerRole } from '../../../core/session';
import { useAsyncAction } from '../../../commonComponents/Hooks';
import Loading from '../../../commonComponents/Loading';
import { stripePublicKey } from '../../../config.local';
import {
	completeDataCaptureActivity,
	getWorkflow,
	partialUpdateWorkflow,
	sendEvent,
	setCandidateConsent
} from '../../../core/api';
import { evalBoolean } from '../../../core/ast';
import {
	candidateDataCaptureServiceRequestTypeCode,
	ccidv,
	cust,
	dashboardUrl,
	dataCaptureCompletedErrorMessage,
	crdServiceRequestType,
	idvServiceRequestType,
	isMitekActivity,
	rtwServiceRequestType,
	thirdPartyCandidateDataCaptureActivityTypeCode
} from '../../../core/constants';
import { setFormSpecComponentIdsToBeUsedAsReactKeys } from '../../../core/domain';
import { EventTypes, finishedStatuses } from '../../../core/enums';
import {
	createQueryString,
	deepCopy,
	getCustomerById,
	getNestedFieldValue,
	getUniqueQueryString,
	hasValue,
	isClassObject,
	isNullOrEmptyString,
	setNestedFieldValue
} from '../../../lib/utils';
import FormSpecification, { FormSpecificationPage } from '../../../models/be/db/FormSpecification';
import Event from '../../../models/be/Event';
import Workflow from '../../../models/be/Workflow';
import FlowLayout from '../../layouts/FlowLayout';
import InvalidOrExpired from '../../pages/InvalidOrExpired';
import PrimaryButton from '../buttons/PrimaryButton';
import SecondaryButton from '../buttons/SecondaryButton';
import { getCurrentFlowProgressBarName, getFlowProgressBarNames } from '../flowProgressBarHelpers';
import MessageBox from '../MessageBox';
import YesNoDialog from '../Dialogs/YesNoDialog';
import './DataCapturePage.less';
import DataCaptureSection from './DataCaptureSection';
import {
	filterOutFieldsThatAreInvisibleInPage,
	getFormSpecificationPage,
	getInitialValuesForPage,
	saveWorkflowData,
	validateFormSpecificationPageSections
} from './form';
import FormError, { FormErrorValues } from './FormError';
import ValidationSummary from './ValidationSummary';
import Buttons from '../Buttons';
import { useAppSelector } from '../../../core/appReducer';
import './DataCaptureSection.less';
import DigitalSignatureField from '../../../commonComponents/fields/DigitalSignatureField';
import ConsentSignature from '../../../models/be/ConsentSignature';
import { isRefWorkflow, getUkrtwActivity } from '../../../core/workflows';
import RootState from '../../../models/RootState';
import { customerContext } from '../../../contexts/customer/CustomerContext';
import { getClientCustomer } from '../../../core/customerApi';
import Customer from '../../../models/be/Customer';
import { getCountryName } from '../../../core/countryList';

// Identity-document "Question" custom fields (e.g. "Do you have a Driver Licence?") and the field(s) that
// must be cleared whenever the candidate's answer no longer justifies keeping them. A candidate reviewing a
// previously-failed document check can answer No without ever touching that document's own fields, so its
// stale (and possibly DVS-failed) values must be cleared here explicitly - the section's visible AST hiding
// it is not enough on its own to stop it being silently resubmitted unchanged.
const idDocumentClearRules: { questionFieldName: string; expectedValue: boolean; fieldsToClear: string[] }[] = [
	// CRIM, AFP, PCRM, VCRM, 100P, DBS/DDBS variants, RTW, NZPV.
	{ questionFieldName: 'customFields.passportQuestion', expectedValue: false, fieldsToClear: ['passportDetails'] },
	{
		questionFieldName: 'customFields.driverLicenceQuestion',
		expectedValue: false,
		fieldsToClear: ['driverLicenceDetails']
	},
	{
		questionFieldName: 'customFields.birthCertificateQuestion',
		expectedValue: false,
		fieldsToClear: ['birthDetails']
	},
	{
		questionFieldName: 'customFields.citizenshipCertificateQuestion',
		expectedValue: false,
		fieldsToClear: ['citizenshipCertificateDetails']
	},
	{ questionFieldName: 'customFields.medicareCardQuestion', expectedValue: false, fieldsToClear: ['medicareDetails'] },
	{
		questionFieldName: 'customFields.bankCardOrCreditCardQuestion',
		expectedValue: false,
		fieldsToClear: ['additionalDocuments.bankCardOrCreditCard']
	},
	// AFP: the visa fields only apply when the candidate confirms they hold an Australian visa.
	{
		questionFieldName: 'customFields.hasAustralianVisaQuestion',
		expectedValue: false,
		fieldsToClear: ['passportDetails.visaPassportNumber', 'passportDetails.visa']
	},
	// NZMOJ uses "drivingLicenceQuestion" instead of "driverLicenceQuestion", and shows the passport section
	// only when the candidate has said they don't have a NZ driver licence (the inverse of every other check).
	{
		questionFieldName: 'customFields.drivingLicenceQuestion',
		expectedValue: false,
		fieldsToClear: ['driverLicenceDetails']
	},
	{ questionFieldName: 'customFields.drivingLicenceQuestion', expectedValue: true, fieldsToClear: ['passportDetails'] }
];

// Clears a document field without ever nulling out a group object itself (e.g. "passportDetails") - some AST
// expressions elsewhere in the form spec reference a leaf field within a group directly (e.g.
// driverLicenceDetails' visibility checks "passportDetails.expiry"), and assume the group is always an
// object with nullable leaf properties. Nulling the group itself makes those lookups return undefined instead
// of null, which the AST interpreter treats as "Symbol does not exist" and throws.
const clearIdDocumentField = (
	values: unknown,
	fieldName: string,
	setValue: (targetFieldName: string, value: null) => void
) => {
	const currentValue = getNestedFieldValue(values, fieldName);
	if (isClassObject(currentValue)) {
		Object.keys(currentValue).forEach((key) => setValue(`${fieldName}.${key}`, null));
	} else if (currentValue !== null) {
		setValue(fieldName, null);
	}
};

const clearUnselectedIdDocumentFields = (values: unknown) => {
	const updatedValues = deepCopy(values);
	idDocumentClearRules.forEach(({ questionFieldName, expectedValue, fieldsToClear }) => {
		if (getNestedFieldValue(updatedValues, questionFieldName) === expectedValue) {
			fieldsToClear.forEach((fieldName) =>
				clearIdDocumentField(updatedValues, fieldName, (path, value): void =>
					setNestedFieldValue(updatedValues, path, value)
				)
			);
		}
	});
	return updatedValues;
};

// Renders nothing - it just watches its one rule's own "Question" field in the live Formik state and clears
// its paired detail field(s) as soon as that specific answer no longer justifies keeping them, so the
// on-screen form reflects the change immediately rather than only once the page is saved. Each rule gets its
// own component instance (and so its own effect with its own dependency) so that, say, answering No to
// "Passport" can never also re-trigger clearing for "Driver Licence" just because that question also
// currently reads No - only the rule whose own question value actually changed reacts.
const IdDocumentFieldClearer = (props: { rule: (typeof idDocumentClearRules)[number] }): null => {
	const { questionFieldName, expectedValue, fieldsToClear } = props.rule;
	const { values, setFieldValue } = useFormikContext<unknown>();
	const questionValue = getNestedFieldValue(values, questionFieldName);

	React.useEffect(() => {
		if (questionValue === expectedValue) {
			fieldsToClear.forEach((fieldName) => clearIdDocumentField(values, fieldName, setFieldValue));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [questionValue]);

	return null;
};

const IdDocumentFieldsAutoClear = (): JSX.Element => (
	<>
		{idDocumentClearRules.map((rule) => (
			<IdDocumentFieldClearer key={`${rule.questionFieldName}-${rule.expectedValue}`} rule={rule} />
		))}
	</>
);

const DataCapturePage = () => {
	const referencePages = 2;
	const { pageSlug } = useParams<{ pageSlug: string }>();
	const location = useLocation();
	const navigate = useNavigate();
	const fieldNamesForFilesBeingUploaded = useAppSelector((state) => state.fieldNamesForFilesBeingUploaded);
	const customerDetails = useSelector((state: RootState) => state?.customerDetails);
	const context = React.useContext(customerContext);
	const mounted = React.useRef(false);
	// This is used to display the loading animation inside of the Formik form.
	const [loading, errorMessage, performAsyncAction] = useAsyncAction(true);
	// This is used to reset Formik for page transitions and display the loading animation outside of the Formik form.
	const [loadingPage, setLoadingPage] = React.useState(true);
	const [dialog, setDialog] = React.useState(false);
	const [formSpecification, setFormSpecification] = React.useState<FormSpecification>(null);
	const [formSpecificationPage, setFormSpecificationPage] = React.useState<FormSpecificationPage>(null);
	const [initialValues, setInitialValues] = React.useState<Workflow>(null);
	const [workflow, setWorkflow] = React.useState<Workflow>(null);
	const [stripe, setStripe] = React.useState<stripe.Stripe>(null);
	const [submitButtonsDisabled, setSubmitButtonsDisabled] = React.useState(false);
	const [backButtonsDisabled, setBackButtonsDisabled] = React.useState(false);

	const [showConsentNoticeDialog, setShowConsentNoticeDialog] = React.useState(false);
	const [successMessage, setSuccessMessage] = React.useState<string>(null);
	const [eSignatureMessage, setESignatureMessage] = React.useState<string>(null);
	const [signatureUploaded, setSignatureUploaded] = React.useState(false);
	const [localConsentSignature, setLocalConsentSignature] = React.useState<ConsentSignature>(null);
	const [customerPublicDisplayName, setCustomerPublicDisplayName] = React.useState<string>(null);

	const workflowId = getUniqueQueryString(location.search, 'workflowId');
	const activityId = getUniqueQueryString(location.search, 'activityId');
	const customerName = () => {
		if (hasValue(customerDetails?.publicDisplayName)) return customerDetails.publicDisplayName;
		if (workflow) return getCustomerById(context.customer.value, workflow.clientCustomerId)?.publicDisplayName;
		return null;
	};

	type ResponseType = {
		workflow: Workflow;
		formSpecification: FormSpecification;
	};

	const documentScanServiceRequestTypes = [idvServiceRequestType, rtwServiceRequestType, crdServiceRequestType];
	const hasMitekRequestCode = workflow?.activities?.some(
		(a) =>
			isMitekActivity(a.providerType.serviceProvider) &&
			documentScanServiceRequestTypes.includes(a.providerType.serviceRequestType)
	);
	const hasQualificationCheck = workflow?.workflowTypeCode === 'QUAL';
	React.useEffect(() => {
		performAsyncAction(async () => {
			let currentWorkflow = workflow;

			if (!workflow) {
				const newWorkflow = await getWorkflow(workflowId);
				currentWorkflow = newWorkflow;
				setWorkflow(newWorkflow);
			}

			const clientCustomerId = customerName();
			if (!hasValue(clientCustomerId) && currentWorkflow) {
				const clientCustomer: Customer = await getClientCustomer(currentWorkflow.clientCustomerId, null);
				setCustomerPublicDisplayName(clientCustomer.publicDisplayName);
			}

			const event: Event = {
				type: EventTypes.AccessedDataCapturePage,
				workflowId,
				activityId,
				subCustomerId: currentWorkflow?.subCustomerId,
				candidateId: currentWorkflow?.candidateId
			};

			const { workflow: workflowValue, formSpecification: formSpecificationValue }: ResponseType = await sendEvent(
				event,
				createQueryString({ pageTitle: pageSlug })
			);

			// After candidate retry CCIDV, candidate will be redirected to consent page. We should redirect candidate back to
			// Dashboard page if the candidate datacapture activity is completed.
			if (workflowValue && hasCandidateRole() && isCandidateDataCaptured(workflowValue)) {
				navigate('/dashboard', { state: { workflowId: workflowValue.id } });
			}

			setFormSpecComponentIdsToBeUsedAsReactKeys(formSpecificationValue);
			setWorkflow(workflowValue);
			setFormSpecification(formSpecificationValue);
			if (!signatureUploaded) setESignatureMessage('Please sign the consent form');
			const stateWasSet = setFormSpecificationPageAndInitialValues(workflowValue, formSpecificationValue);
			if (!stateWasSet) {
				const firstPageSlug = formSpecificationValue.pages[0].slug;
				changeToDataCapturePage(firstPageSlug);
			} else {
				setLoadingPage(false);
			}
		});

		// Attribution: https://github.com/stripe/react-stripe-elements#advanced-integrations
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((window as any).Stripe) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			setStripe((window as any).Stripe(stripePublicKey));
		} else {
			document.querySelector('#stripe-js').addEventListener('load', () => {
				// Create Stripe instance once Stripe.js loads
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				setStripe((window as any).Stripe(stripePublicKey));
			});
		}

		// needed to set entire body to background color
		document.body.classList.add('dataCapturePage');
		return () => {
			document.body.classList.remove('dataCapturePage');
		};
	}, []);

	React.useEffect(() => {
		// Should set back to default value if page slug changes.
		setSubmitButtonsDisabled(false);
		setBackButtonsDisabled(false);

		if (!mounted.current) {
			mounted.current = true;
		} else {
			// Must set loading to true before setting states of initialValues for formik, otherwise the page will be broken
			// during page transition. And set loading back to false only after setState is done.
			performAsyncAction(async () => {
				setFormSpecificationPageAndInitialValues(workflow, formSpecification);
				setLoadingPage(false);
			});
		}
	}, [pageSlug]);

	const setFormSpecificationPageAndInitialValues = (newWorkflow: Workflow, newFormSpecification: FormSpecification) => {
		const newFormSpecificationPage = getFormSpecificationPage(newFormSpecification, pageSlug);
		if (newFormSpecificationPage === null) {
			return false;
		}
		const newInitialValues = getInitialValuesForPage(newFormSpecificationPage, newWorkflow);

		setFormSpecificationPage(newFormSpecificationPage);
		setInitialValues(newInitialValues);
		return true;
	};

	const changeToDataCapturePage = (newPageSlug: string) => {
		setLoadingPage(true);
		const url = `/check/${newPageSlug}${createQueryString({ workflowId, activityId })}`;
		navigate(url);
	};

	const handleBackButtonClick = (formikProps: FormikProps<unknown>) => async () => {
		await performAsyncAction(async () => {
			if (formikProps.dirty) {
				await saveData(formikProps.values);
			}

			await conditionallyChangePageAndCompleteDataCapture(-1);
		});
	};

	const handleNextButtonClick = (formikProps: FormikProps<unknown>) => async () => {
		await conditionallySaveData(formikProps, async () => await conditionallyChangePageAndCompleteDataCapture(1));
	};

	const saveData = async (values: unknown, removeInvisibleFields = false) => {
		try {
			const valuesToSave = removeInvisibleFields ? clearUnselectedIdDocumentFields(values) : values;
			const updatedWorkflow = await saveWorkflowData(
				workflow,
				valuesToSave,
				activityId,
				formSpecificationPage,
				removeInvisibleFields
			);
			setWorkflow(updatedWorkflow);
			// Update the initialValues as well as changing the workflow will cause Formik to be reset.
			setInitialValues(getInitialValuesForPage(formSpecificationPage, updatedWorkflow));
		} catch (e) {
			if (hasValue(e.body)) throw new Error(`Failed to save data: ${e.body}`);
			throw e;
		}
	};

	const generateClouldCheckLink = async (id: string): Promise<string> => {
		const event: Event = { workflowId: id, type: EventTypes.GenerateIDVLink };
		const timeoutInMilliseconds = 10 * 1000;
		return await sendEvent(event, '', timeoutInMilliseconds);
	};

	const updateIdVerificationTypeInWorkflow = async (idvType?: string) => {
		performAsyncAction(async () => {
			const partialObject: Record<string, unknown> = {
				clientCustomerId: workflow.clientCustomerId
			};
			partialObject.customFields = { ...workflow.customFields, idVerificationType: idvType };
			await partialUpdateWorkflow(workflow.id, activityId, partialObject);
		});
	};

	const conditionallyChangePageAndCompleteDataCapture = async (pageIndexOffset: number) => {
		const currentPageSlug = formSpecificationPage.slug;
		if (pageIndexOffset === 1) {
			if (currentPageSlug === 'consent') {
				await acceptConsent();
				if (workflow?.customFields?.idVerificationType === 'CIDV') await updateIdVerificationTypeInWorkflow(null); // Reset IDV Selection only when CIDV is selected
			} else if (isLastPage()) {
				const event = {
					type: EventTypes.CandidateCompletedDataCapturePage,
					workflowId: workflow.id,
					clientCustomerId: workflow.clientCustomerId
				} as Event;
				await sendEvent(event, createQueryString({ pageTitle: formSpecificationPage.title }));
			}
		}
		const pageSlugs = formSpecification.pages.map((p) => p.slug);
		const index = pageSlugs.indexOf(currentPageSlug) + pageIndexOffset;

		await performAsyncAction(async () => {
			if (isRefWorkflow(workflow.workflowTypeCode) && index === referencePages) {
				const queryString = createQueryString({ workflowId: workflow.id });
				navigate(`/references/timeline${queryString}`);
			} else if (index < 0) {
				navigate(dashboardUrl);
			} else if (index < pageSlugs.length) {
				const newPageSlug = pageSlugs[index];
				changeToDataCapturePage(newPageSlug);
			} else {
				const updatedWorkflow = await completeDataCaptureActivity(workflow.id, activityId, customerName());

				const thridpartyActivity = updatedWorkflow.activities?.find(
					(x) =>
						x.activityTypeCode === thirdPartyCandidateDataCaptureActivityTypeCode ||
						(x.providerType.serviceRequestType === ccidv && !finishedStatuses.includes(x.status))
				);
				let thirdPartyUrl = thridpartyActivity?.thirdPartyUrl?.replace('{workflowId}', `${workflow.id}`);

				if (!thirdPartyUrl) {
					thirdPartyUrl = await generateClouldCheckLink(workflow.id);
				}

				if (thirdPartyUrl) {
					setDialog(true);
					if (thirdPartyUrl.includes('://')) {
						document.location.href = thirdPartyUrl;
					} else {
						navigate(thirdPartyUrl);
					}
				} else navigate(dashboardUrl, { state: { workflowId: workflow.id } });
			}
		});
	};

	const handleResubmitButtonClick = (formikProps: FormikProps<unknown>) => async () => {
		await conditionallySaveData(formikProps, async () => {
			await completeDataCaptureActivity(workflow.id, activityId, customerName());
			travelToReviewSubmissionPage();
		});
	};

	const handleSaveButtonClick = (formikProps: FormikProps<unknown>) => async () => {
		await conditionallySaveData(formikProps, async () => {
			setSuccessMessage('Your changes have been saved, please resubmit the check when your changes are complete');
			const fiveSeconds = 5000;
			setTimeout(() => setSuccessMessage(null), fiveSeconds);
		});
	};

	const handlePartialSave = (formikProps: FormikProps<unknown>) => async () => {
		await saveData(formikProps.values, true);
	};

	const handleCancelButtonClick = () => {
		travelToReviewSubmissionPage();
	};

	const conditionallySaveData = async (formikProps: FormikProps<unknown>, onSuccess: () => Promise<void>) => {
		await performAsyncAction(async () => {
			// formik validateOnMount has been set to true, so formik.isValid is up to date.
			if (formikProps.isValid && !formikProps.dirty) {
				// Submit the form to run handleSubmit in case the additional names were changed in a previous page and the user
				// didn't change any fields in this page, but we need to run the related date validation code.
				await formikProps.submitForm();
				if (
					hasCandidateRole() &&
					(showSignatureComponentOnDataCapture() || showSignatureComponentOnConsent()) &&
					(workflow.consent == null || isNullOrEmptyString(workflow.consent.signature))
				) {
					setESignatureMessage('Please sign the consent form');
					return;
				}
				// Even though the form is not dirty, there could still be invisible fields that need to be removed.
				await saveData(formikProps.values, true);
				await onSuccess();
			} else {
				// We must submit the form when it isn't valid to let formik to set all fields to touched to show errors,
				// even though the submit handler does nothing.
				await formikProps.submitForm();

				if (formikProps.isValid && formikProps.dirty) {
					if (
						hasCandidateRole() &&
						(showSignatureComponentOnDataCapture() || showSignatureComponentOnConsent()) &&
						(workflow.consent == null || isNullOrEmptyString(workflow.consent.signature))
					) {
						setESignatureMessage('Please sign the consent form');
						return;
					}
					await saveData(formikProps.values, true);
					await onSuccess();
				}
			}
		});
	};

	const travelToReviewSubmissionPage = () => {
		navigate(`/candidates/${workflow.candidateId}/check-details/${workflow.id}/screener-review`);
	};

	const isLastPage = () => {
		return (
			formSpecification.pages[formSpecification.pages.length - 1].slug === pageSlug &&
			!isRefWorkflow(workflow.workflowTypeCode)
		);
	};

	const dbsSeWorkflowTypeCodesWithMitekRedirect = ['PSTD', 'PSTDV', 'PENH', 'PENHA', 'PENHV'];

	const GetSubmitButtonText = (values: FormErrorValues) => {
		const idvActivity = getUkrtwActivity(workflow.activities);
		if (idvActivity && values.hasUkOrIrishPassport) return 'Proceed to Digital ID';
		const customFields = values.customFields as Record<string, unknown> | undefined;
		if (
			idvActivity &&
			dbsSeWorkflowTypeCodesWithMitekRedirect.includes(workflow.workflowTypeCode) &&
			hasValue(customFields?.passportOwnershipType)
		)
			return 'Proceed to Digital ID';
		if (
			formSpecification.pages[formSpecification.pages.length - 1].slug === pageSlug &&
			!isRefWorkflow(workflow.workflowTypeCode)
		)
			return 'Submit';
		if (formSpecificationPage.nextButtonText) return formSpecificationPage.nextButtonText;
		return 'Save & Continue';
	};

	const isConsentPage = () => {
		const consentSlug = 'consent';
		return formSpecificationPage.slug === consentSlug;
	};

	const showSignatureComponentOnConsent = () => {
		const consentComponent = formSpecification?.pages
			?.find((x) => x.slug === 'consent')
			?.sections?.flatMap((section) => section.components || [])
			?.find((d) => d.props.type === 'signature');
		return consentComponent && isConsentPage();
	};

	const showSignatureComponentOnDataCapture = () => {
		const candidateComponent = formSpecification?.pages
			?.filter((page) => page.slug !== 'consent')
			?.flatMap((page) => page.sections || [])
			?.flatMap((section) => section.components || [])
			?.find((component) => component.props?.type === 'signature');

		return candidateComponent && !isConsentPage() && isLastPage();
	};

	const isAdditionalNamesPage = () => {
		const nameSlug = 'names';
		return formSpecificationPage.slug === nameSlug;
	};

	const declineConsent = async () => {
		await setCandidateConsent(false, workflowId);
		closeConsentNoticeDialog();
		await conditionallyChangePageAndCompleteDataCapture(-1);
	};

	const acceptConsent = async () => {
		await setCandidateConsent(true, workflowId);
	};

	const openConsentNoticeDialog = () => {
		setShowConsentNoticeDialog(true);
	};

	const closeConsentNoticeDialog = () => {
		setShowConsentNoticeDialog(false);
	};

	const navigateToPageUsingFlowProgressName = (values: unknown) => async (flowProgressBarName: string) => {
		const newPageSlug = formSpecification.pages?.find((p) => p.flowProgressBarName === flowProgressBarName).slug;
		navigateToPageUsingSlug(values)(newPageSlug);
	};

	const navigateToPageUsingSlug = (values: unknown) => async (newPageSlug: string) => {
		await performAsyncAction(async () => {
			await saveData(values);
			changeToDataCapturePage(newPageSlug);
		});
	};

	const handleConsentUpload = (uploadedConsentSignature: ConsentSignature) => {
		if (workflow.consent) {
			workflow.consent.signature = uploadedConsentSignature?.imageUrl;
		} else {
			workflow.consent = {
				signature: uploadedConsentSignature?.imageUrl,
				signedTimestamp: uploadedConsentSignature?.consentTime,
				ipAddress: uploadedConsentSignature?.ipAddress,
				address: '',
				overallConsent: false
			};
		}
		if (uploadedConsentSignature?.imageDataUrl) {
			sessionStorage.setItem(`consentSignature_${workflowId}`, uploadedConsentSignature.imageDataUrl);
		}
		setLocalConsentSignature(uploadedConsentSignature);
		setSignatureUploaded(true);
	};

	// Render code
	if (errorMessage !== null) {
		if (errorMessage.includes('Unauthorized') || errorMessage.includes(dataCaptureCompletedErrorMessage)) {
			return <InvalidOrExpired />;
		}
	}

	if (errorMessage) return <MessageBox error={errorMessage} />;

	if (loadingPage) {
		// Must show loading outside of Formik so that Formik will re-render
		// and the validation code will run on page change.
		return <Loading />;
	}

	const cachedImageDataUrl = workflow.consent?.signature
		? sessionStorage.getItem(`consentSignature_${workflowId}`)
		: null;
	const consentSignature: ConsentSignature = localConsentSignature ?? {
		consentTime: workflow.consent?.signedTimestamp,
		imageUrl: workflow.consent?.signature,
		ipAddress: workflow.consent?.ipAddress,
		imageDataUrl: cachedImageDataUrl ?? undefined
	};
	const Validate =
		(formSpecPage: FormSpecificationPage) =>
		(values: unknown): FormikErrors<unknown> => {
			const { sections } = formSpecPage;
			const validationErrors = validateDateOfBirthAndAddressHistory(values as Workflow, formSpecPage);
			if (validationErrors !== null && Object.keys(validationErrors).length > 0) return validationErrors;
			return validateFormSpecificationPageSections(sections)(values);
		};

	const validateDateOfBirthAndAddressHistory = (values: Workflow, formSpecPage: FormSpecificationPage) => {
		// TODO: This is field specific validation code that should go somewhere else.
		/* TODO: replace all of this hard coded logic with a new validationRules field in the form spec page like this:
			validationRules: [
			 {
				rule: [ lessThan, "birthDetails.birthDate", [ min [ map, "addressHistories", "x.fromDate"] ] ],
				errorMessage: "Birth Date in Birth Details must not be after the address history date(s).'" }
			]
		*/
		const errors: FormikErrors<Workflow> = {};

		const workflowWithVisibleFields = filterOutFieldsThatAreInvisibleInPage(workflow, values, formSpecPage);

		const earliestAddressDateString = workflow.addressHistories
			?.map((x) => x.fromDate)
			.sort((a, b) => Date.parse(a) - Date.parse(b))[-2];
		const earliestAddressDateInMilliseconds = Date.parse(earliestAddressDateString);
		const earliestNameDateString = workflow.additionalNames
			?.map((x) => x.fromDate)
			.sort((a, b) => Date.parse(a) - Date.parse(b))[-2];
		const earliestNameDateInMilliseconds = Date.parse(earliestNameDateString);
		const isBirthDetailsBirthDateVisibleAndHasValue =
			hasValue(workflowWithVisibleFields.birthDetails?.birthDate) && hasValue(values.birthDetails?.birthDate);
		const birthDetailsBirthDateInMilliseconds = isBirthDetailsBirthDateVisibleAndHasValue
			? Date.parse(values.birthDetails.birthDate)
			: Number.MIN_SAFE_INTEGER;

		if (earliestAddressDateInMilliseconds < birthDetailsBirthDateInMilliseconds) {
			errors.birthDetails = { birthDate: 'Birth Date in Birth Details must not be after the additional name date(s).' };
		}
		if (earliestNameDateInMilliseconds < birthDetailsBirthDateInMilliseconds) {
			errors.birthDetails = { birthDate: 'Birth Date in Birth Details must not be after the additional name date(s).' };
		}

		const isPassportBirthDateVisibleAndHasValue =
			hasValue(workflowWithVisibleFields.passportDetails?.birthDate) && hasValue(values.passportDetails?.birthDate);

		const passportBirthDateInMilliseconds = isPassportBirthDateVisibleAndHasValue
			? Date.parse(values.passportDetails.birthDate)
			: Number.MIN_SAFE_INTEGER;
		if (earliestAddressDateInMilliseconds < passportBirthDateInMilliseconds) {
			errors.passportDetails = { birthDate: 'Birth Date in Passport must not be after the address history date(s).' };
		}

		if (earliestNameDateInMilliseconds < passportBirthDateInMilliseconds) {
			errors.passportDetails = { birthDate: 'Birth Date in Passport must not be after the address history date(s).' };
		}

		if (
			isPassportBirthDateVisibleAndHasValue &&
			isBirthDetailsBirthDateVisibleAndHasValue &&
			workflowWithVisibleFields.passportDetails?.birthDate !== workflowWithVisibleFields.birthDetails.birthDate
		) {
			errors.passportDetails = { birthDate: 'Please ensure your DOB matches across all submitted details.' };
		}

		const isDriverLicenceVisibleAndHasValue =
			hasValue(workflowWithVisibleFields.driverLicenceDetails?.birthDate) &&
			hasValue(values.driverLicenceDetails?.birthDate);

		const driverLicenceBirthDateInMilliseconds = isDriverLicenceVisibleAndHasValue
			? Date.parse(values.driverLicenceDetails.birthDate)
			: Number.MIN_SAFE_INTEGER;

		if (earliestAddressDateInMilliseconds < driverLicenceBirthDateInMilliseconds) {
			errors.driverLicenceDetails = {
				birthDate: 'Birth Date in Driver Licence must not be after the additional name date(s).'
			};
		}

		if (earliestNameDateInMilliseconds < driverLicenceBirthDateInMilliseconds) {
			errors.driverLicenceDetails = {
				birthDate: 'Birth Date in Driver Licence must not be after the additional name date(s).'
			};
		}

		if (
			isDriverLicenceVisibleAndHasValue &&
			isBirthDetailsBirthDateVisibleAndHasValue &&
			workflowWithVisibleFields.driverLicenceDetails?.birthDate !== workflowWithVisibleFields.birthDetails.birthDate
		) {
			errors.driverLicenceDetails = { birthDate: 'Please ensure your DOB matches across all submitted details.' };
		}
		return errors;
	};

	return (
		<Formik
			initialValues={initialValues}
			enableReinitialize // This is so that the initialValues can be updated for page transitions.
			validateOnMount
			onSubmit={() => {}}
			validate={Validate(formSpecificationPage)}
		>
			{(formikProps: FormikProps<FormErrorValues>) => {
				if (loading || dialog) {
					return <Loading />;
				}

				const { values } = formikProps;
				const uploadingAFile = fieldNamesForFilesBeingUploaded.length > 0;

				const countryName = getCountryName(
					workflow.icrdDetails?.creditCheckCountry ?? workflow.icrmDetails?.criminalCheckCountry
				);

				return (
					<FlowLayout
						title={formSpecificationPage.title}
						navigateToPage={navigateToPageUsingFlowProgressName(values)}
						flowProgressBarNames={getFlowProgressBarNames(formSpecification.pages)}
						showFlowProgressBarLine={hasCandidateRole()}
						currentFlowProgressBarName={getCurrentFlowProgressBarName(pageSlug, formSpecification.pages)}
						workflowServiceName={workflow.candidateWorkflowServiceName ?? workflow.workflowServiceName}
						country={countryName}
					>
						{workflow.introductoryText && !isConsentPage() && (
							<Introduction>
								<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(workflow.introductoryText) }} />
							</Introduction>
						)}
						{formSpecificationPage.heading && <h1>{formSpecificationPage.heading}</h1>}
						<MessageBox successMessage={successMessage} />
						{isAdditionalNamesPage() && <MessageBox information={AdditionalNamesWarningText} />}
						<IdDocumentFieldsAutoClear />
						<form
							className="k-form formLayout"
							onSubmit={(e) => {
								e.preventDefault();
							}}
						>
							{formSpecificationPage.sections
								.filter((s) => evalBoolean(s.visible, values))
								.map((s) => (
									<DataCaptureSection
										key={s.id}
										section={s}
										values={values}
										candidateId={workflow.candidateId}
										workflowId={workflow.id}
										visibleWorkflowFields={filterOutFieldsThatAreInvisibleInPage(
											workflow,
											values,
											formSpecificationPage
										)}
										navigateToPage={navigateToPageUsingSlug(formikProps.values)}
										stripe={stripe}
										setSubmitButtonsDisabled={setSubmitButtonsDisabled}
										setBackButtonsDisabled={setBackButtonsDisabled}
										workflowPrimaryName={workflow.primaryName}
										customerUserName={workflow.customerUserName}
										onFileUpload={handlePartialSave(formikProps)}
										clientustomerId={workflow.clientCustomerId}
										customerName={customerPublicDisplayName}
										formSpecificationPage={formSpecificationPage}
									/>
								))}
							<FormError formikProps={formikProps} />
						</form>
						{pageSlug === 'id' &&
							Object.keys(formikProps.touched).length > 0 &&
							Object.keys(formikProps.errors).length > 0 && (
								<ValidationSummary errors={formikProps.errors} touched={formikProps.touched} />
							)}
						{hasCandidateRole() && (showSignatureComponentOnDataCapture() || showSignatureComponentOnConsent()) && (
							<>
								<SignatureWrapper>
									<div className="sectionContainer">
										<DigitalSignatureField
											email={workflow.candidateEmail}
											workflowId={workflow.id}
											consentSignature={consentSignature}
											candidateId={workflow.candidateId}
											customerId={workflow.clientCustomerId}
											onUpload={handleConsentUpload}
											errorMessage={eSignatureMessage}
											setSignatureUploaded={setSignatureUploaded}
										/>
									</div>
								</SignatureWrapper>
							</>
						)}
						{hasMitekRequestCode && (
							<>
								Please ensure you have your passport (or equivalent ID) readily available before proceeding to the next
								step
							</>
						)}
						{hasQualificationCheck && isConsentPage() && (
							<DeclineInformation>
								If you do not have a tertiary education qualification please press I Decline below
							</DeclineInformation>
						)}
						<Buttons>
							{hasCandidateRole() && (
								<>
									<SecondaryButton
										text="Back"
										onClick={handleBackButtonClick(formikProps)}
										className="secondaryButton"
										disabled={workflow.workflowTypeCode === cust ? false : backButtonsDisabled || uploadingAFile}
									/>
									{isConsentPage() && (
										<SecondaryButton
											text="I Decline"
											onClick={openConsentNoticeDialog}
											className="secondaryButton"
											disabled={submitButtonsDisabled || uploadingAFile}
										/>
									)}
									<PrimaryButton
										onClick={handleNextButtonClick(formikProps)}
										text={GetSubmitButtonText(values)}
										disabled={
											submitButtonsDisabled ||
											uploadingAFile ||
											(hasCandidateRole() &&
												(showSignatureComponentOnDataCapture() || showSignatureComponentOnConsent()) &&
												!signatureUploaded)
										}
										className="nextButton"
									/>
								</>
							)}
							{hasCustomerRole() && (
								<>
									<SecondaryButton
										onClick={handleCancelButtonClick}
										text="Cancel"
										disabled={submitButtonsDisabled || uploadingAFile}
									/>
									<SecondaryButton
										onClick={handleSaveButtonClick(formikProps)}
										text="Save"
										disabled={submitButtonsDisabled || uploadingAFile}
									/>
									<PrimaryButton
										onClick={handleResubmitButtonClick(formikProps)}
										text="Resubmit"
										disabled={submitButtonsDisabled || uploadingAFile}
									/>
								</>
							)}
						</Buttons>
						{showConsentNoticeDialog && (
							<YesNoDialog
								message="Declining consent will prevent you from completing all required checks. Are you sure you want to proceed?"
								yesButtonText="Decline"
								noButtonText="Cancel"
								onYes={declineConsent}
								onNo={closeConsentNoticeDialog}
							/>
						)}
					</FlowLayout>
				);
			}}
		</Formik>
	);
};

export default DataCapturePage;

const AdditionalNamesWarningText = 'All Middle names must be added and pre-filled names carefully checked';

const isCandidateDataCaptured = (workflow: Workflow) => {
	const candidateActivity = workflow.activities?.find(
		(a) => a.providerType.serviceRequestType === candidateDataCaptureServiceRequestTypeCode
	);
	if (candidateActivity) return candidateActivity.dataCaptured;
	return false;
};

export const SignatureWrapper = styled.div`
	border: 1px solid rgba(38, 38, 38, 0.2);
	border-radius: 8px;
	background-color: #fff;
	padding: 24px 17px 32px 17px;
`;

const Introduction = styled.div`
	padding-top: 30px;
	font-size: 15px;
	line-height: 20px;
	color: #262626;
	width: 100%;
	text-align: center;
	margin: auto;
`;

const DeclineInformation = styled.div`
	padding-top: 15px;
	font-weight: bold;
	line-height: 20px;
	color: red;
`;
